import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { match } from "ts-pattern";

import { SubmissionButton } from "@thalamos/frontend";
import type {
  AvailableLanguages,
  UserOrganisationMembership,
} from "@thalamos/vision-common";
import { tk } from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import BTPLogoImg from "../../assets/img/btp_logo_blue.svg";
import ColpLogoImg from "../../assets/img/colp_logo_black.svg";
import METLogoImg from "../../assets/img/met_logo.svg";
import { Typeahead } from "../../components/Typeahead/Typeahead.js";
import { config } from "../../config/index.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

export function ContextSelectorInnerPage({
  memberships,
  roleOptions,
  refetchUser,
  language,
}: {
  memberships: UserOrganisationMembership[];
  roleOptions: { label: string; value: string }[];
  refetchUser: ReturnType<typeof apiHooks.user.me>[1];
  language?: AvailableLanguages;
}) {
  const [selectedRole, setSelectedRole] = useState("");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notifications = useToastNotifications();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const selectRoleMatch = memberships.some(
    (o) => o.role === selectedRole.toLowerCase(),
  );

  const handleClick = async () => {
    const selectedMembership = memberships.find(
      (m) => m.role === selectedRole.toLowerCase(),
    );
    if (!selectedMembership) {
      notifications.show(t("contextSelector.invalidRole"), {
        severity: "error",
      });
      return;
    }

    const response = await api.user.setSessionContext(selectedMembership);
    if (response.status !== 204) {
      notifications.show(t("contextSelector.setContextError"), {
        severity: "error",
      });
      return;
    }

    await refetchUser();
    navigate(createRoute.home());
    return;
  };

  const logoComponent = useMemo(() => {
    return match(language)
      .with("enMet", () => METLogoImg)
      .with("enBtp", "en", () => BTPLogoImg)
      .with("enColp", () => ColpLogoImg)
      .with(undefined, () => undefined)
      .exhaustive();
  }, [language]);

  return (
    <Box
      sx={{
        minHeight: "80vh",
        width: "100%",
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Paper sx={{ p: 4, minWidth: { md: 600 } }}>
        {logoComponent && (
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              justifyContent: "center",
              alignItems: "center",
              height: isDesktop ? "5rem" : "4rem",
              marginBottom: "1em",
            }}
          >
            <img
              src={logoComponent}
              alt={"Logo"}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "auto",
              }}
            />
          </Box>
        )}
        <Typography sx={{ marginBottom: "1em" }}>
          {t("contextSelector.welcome")}
        </Typography>

        <Typography sx={{ marginBottom: "1em" }}>
          {t("contextSelector.chooseRole")}
        </Typography>

        <Formik initialValues={{ selectedRole }} onSubmit={() => {}}>
          {() => (
            <Form>
              <Typeahead
                inputValue={selectedRole}
                placeholder={{
                  translationKey: tk("contextSelector.selectOption"),
                }}
                name={"role"}
                label={{ translationKey: tk("contextSelector.role") }}
                fieldName="role"
                onInputChange={setSelectedRole}
                options={roleOptions}
                optionsKey="label"
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.label
                }
              />
            </Form>
          )}
        </Formik>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            marginTop: 3.2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              window.location.href = `${config.backendUrl}/api/oauth/logout`;
            }}
          >
            {t("navigation.logout")}
          </Button>

          <SubmissionButton
            variant="contained"
            onClick={handleClick}
            disabled={!selectRoleMatch}
          >
            {t("contextSelector.continue")}
          </SubmissionButton>
        </Box>
      </Paper>
    </Box>
  );
}

export function ContextSelector() {
  const [{ data, loading, error }, refetchUser] = apiHooks.user.me();

  if (loading || data === undefined) {
    return <CircularProgress />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  return (
    <ContextSelectorInnerPage
      memberships={data.memberships}
      refetchUser={refetchUser}
      roleOptions={data.memberships.map((m) => ({
        label: m.role.charAt(0).toUpperCase() + m.role.slice(1),
        value: m.role,
      }))}
      language={data.sessionOrganisationConfiguration?.language}
    />
  );
}
